import { IonItem } from '@ionic/react';
import { Select, Space } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TEST_IDS } from '../../../config/utils/constants';
import { RECEIVING_NOTE_PURPOSE } from '../../../interfaces/myData.interface';
import { isQuantityReceiptForm, isQuantityReceiptFormRelated } from '../../../services/utils/myDataUtils';

interface Props {
  receivingNotePurposeField: any;
  otherReceivingNotePurposeTitleField: any;
  formik: any;
}

export const DocQuantityReceipt = ({
  receivingNotePurposeField,
  otherReceivingNotePurposeTitleField,
  formik,
}: Props) => {
  const { t } = useTranslation();

  const documentType = useMemo(
    () => formik?.values?.documentTypeId?.object?.aadeCode,
    [formik?.values?.documentTypeId?.object?.aadeCode],
  );
  const isVisible = useMemo(() => isQuantityReceiptForm(documentType), [documentType]);

  const receivingNotePurposeValue: RECEIVING_NOTE_PURPOSE | null = formik?.values?.[receivingNotePurposeField?.name];
  const otherReceivingNotePurposeTitleValue: string | null =
    formik?.values?.[otherReceivingNotePurposeTitleField?.name];

  const [newOption, setNewOption] = useState<{ value: number; label: string } | null>(null);

  // Pre-populate synthetic option when editing an invoice with purpose OTHER
  useEffect(() => {
    if (receivingNotePurposeValue === RECEIVING_NOTE_PURPOSE.OTHER && otherReceivingNotePurposeTitleValue) {
      setNewOption({ value: RECEIVING_NOTE_PURPOSE.OTHER, label: otherReceivingNotePurposeTitleValue });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear both fields when doc type changes away from QRF
  useEffect(() => {
    if (!isVisible) {
      formik.setFieldValue(receivingNotePurposeField?.name, null);
      formik.setFieldValue(otherReceivingNotePurposeTitleField?.name, null);
      setNewOption(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  // QUANTITY_CHECK (5) is only valid for 10.1 — clear it when switching to 10.2
  useEffect(() => {
    if (isVisible && !isQuantityReceiptFormRelated(documentType) && receivingNotePurposeValue === RECEIVING_NOTE_PURPOSE.QUANTITY_CHECK) {
      formik.setFieldValue(receivingNotePurposeField?.name, null);
      formik.setFieldValue(otherReceivingNotePurposeTitleField?.name, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentType]);

  const staticOptions = useMemo(
    () => [
      { value: RECEIVING_NOTE_PURPOSE.NON_OBLIGATORY_ISSUER, label: t('SALE.RECEIVING_NOTE_PURPOSE_1') },
      { value: RECEIVING_NOTE_PURPOSE.DENIAL_OR_OMISSION_OF_ISSUANCE, label: t('SALE.RECEIVING_NOTE_PURPOSE_2') },
      { value: RECEIVING_NOTE_PURPOSE.INTRA_COMMUNITY_ACQUISITION, label: t('SALE.RECEIVING_NOTE_PURPOSE_3') },
      { value: RECEIVING_NOTE_PURPOSE.ACQUISITION_FROM_THIRD_COUNTRY, label: t('SALE.RECEIVING_NOTE_PURPOSE_4') },
      // myDATA v2.0.2: QUANTITY_CHECK is only allowed for 10.1, not 10.2
      {
        value: RECEIVING_NOTE_PURPOSE.QUANTITY_CHECK,
        label: t('SALE.RECEIVING_NOTE_PURPOSE_5'),
        disabled: !isQuantityReceiptFormRelated(documentType),
      },
      { value: RECEIVING_NOTE_PURPOSE.NON_OR_PARTIAL_DELIVERY, label: t('SALE.RECEIVING_NOTE_PURPOSE_6') },
    ],
    [t, documentType],
  );

  const options = useMemo(
    () => (newOption ? [...staticOptions, newOption] : staticOptions),
    [staticOptions, newOption],
  );

  const onSearch = useCallback((value: string) => {
    if (value.length > 4 && value.length <= 149) {
      setNewOption({ value: RECEIVING_NOTE_PURPOSE.OTHER, label: value });
    } else if (value.length <= 4) {
      setNewOption(null);
    }
    // > 149: keep the last valid option visible but don't update it
  }, []);

  const onChange = useCallback((value: number | undefined, option: any) => {
    formik.setValues((prev: any) => ({
      ...prev,
      [receivingNotePurposeField?.name]: value ?? null,
      [otherReceivingNotePurposeTitleField?.name]:
        value === RECEIVING_NOTE_PURPOSE.OTHER ? option?.label : null,
    }));
  }, [formik, receivingNotePurposeField?.name, otherReceivingNotePurposeTitleField?.name]);

  const filterOption = useCallback(
    (input: string, option?: { label: string; value: number }) =>
      (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
    [],
  );

  const hasError = useMemo(
    () => formik.touched && formik?.errors?.[receivingNotePurposeField?.name],
    [formik.touched, formik.errors, receivingNotePurposeField?.name],
  );

  const label = useMemo(
    () => `${t('SALE.RECEIVING_NOTE_PURPOSE_LABEL')}${receivingNotePurposeField?.required ? ' *' : ''}`,
    [t, receivingNotePurposeField?.required],
  );

  if (!isVisible) return null;

  return (
    <div>
      <div className="padding-b-16 padding-l-12">{label}</div>
      <div className="flex-center-start-column-nowrap positionRelative width-360px">
        <IonItem
          className={`width-360px border-radius-input g-select-border ${hasError ? 'b-field-border-error' : ''}`}
          mode="md">
          <Select
            id={TEST_IDS.Invoice.receivingNotePurposePicker}
            showSearch
            allowClear
            placeholder={`${t('SALE.RECEIVING_NOTE_PURPOSE_PLACEHOLDER')}...`}
            className="my-ant-select normalText5"
            variant="borderless"
            onSearch={onSearch}
            onChange={onChange}
            options={options}
            optionRender={(option: any) => (
              <Space
                className={
                  option?.data?.value === RECEIVING_NOTE_PURPOSE.OTHER ? 'option-border-new' : 'option-border-mine'
                }>
                {option.label}
              </Space>
            )}
            filterOption={filterOption}
            value={receivingNotePurposeValue ?? null}
          />
        </IonItem>
      </div>
    </div>
  );
};
